import {
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  input,
  linkedSignal,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { BuildingItemDto, BuildingService, SortDto } from '@artemis-software/wr-api';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { NbComponentSize, NbPopoverDirective } from '@nebular/theme';
import { watch } from '@/utils/watch';
import { currentTenantId, isExternalTenant } from '@/utils/admin-utils';
import OrderEnum = SortDto.OrderEnum;

@Component({
  standalone: false,
  selector: 'wr-building-select',
  templateUrl: './building-select.component.html',
  styleUrls: ['./building-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuildingSelectComponent),
      multi: true,
    },
  ],
})
export class BuildingSelectComponent extends AbstractFormControl<BuildingItemDto | undefined> implements OnInit, OnDestroy {

  readonly buildingService = inject(BuildingService);
  readonly router = inject(Router);

  readonly popover = viewChild(NbPopoverDirective);

  readonly input = viewChild('searchInput', { read: ElementRef });

  readonly size = input<NbComponentSize>('medium');

  readonly tenantId = input<string | undefined>();

  private readonly isExternal = isExternalTenant();
  private readonly ownTenantId = currentTenantId();

  readonly resetVersion = signal(0);

  /** @deprecated Use tenantId input signal instead */
  @Input()
  tenantId$?: Observable<string>;

  readonly tenantIdComputed = linkedSignal(this.tenantId);

  readonly filterText = signal('');

  readonly page = signal(0);

  readonly buildings = signal<BuildingItemDto[]>([]);

  readonly destroy$ = new Subject();

  constructor() {
    super();
    watch(this.valueSignal, (building) => {
      this.filterText.set(building?.baseData.sectionText ?? '');
    });
    watch([this.tenantIdComputed, this.filterText], async () => {
      await this.reset();
    });
  }

  override writeValue(building: BuildingItemDto): void {
    super.writeValue(building);
    this.filterText.set(this.viewHandle(building));
  }

  ngOnInit() {
    if (this.tenantId$) {
      this.tenantId$.pipe(takeUntil(this.destroy$)).subscribe((tenantId) => {
        this.tenantIdComputed.set(tenantId);
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onSelectionChange(building?: BuildingItemDto): void {
    if (building?.id) {
      this.filterText.set(this.viewHandle(building));
      this.value = building;
      this.popover()?.hide();
    }
  }

  viewHandle(value?: BuildingItemDto | string): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value && value.baseData.sectionText != undefined) {
      return value.baseData.sectionText;
    }
    return '';
  }

  async clear() {
    this.value = undefined;
    this.filterText.set('');
    await this.reset();
  }

  goToBuilding(): void {
    this.router.navigate(['building', this.value?.id]);
  }

  focus() {
    this.input()?.nativeElement.focus();
  }

  async reset() {
    this.resetVersion.update((v) => v + 1);
    this.buildings.set([]);
    this.page.set(0);
    await this.loadMore();
  }

  async loadMore() {
    const versionAtStart = this.resetVersion();

    const page = this.page();
    this.page.update((p) => p + 1);
    const more = await firstValueFrom(this.buildingService.findAll({
      fullTextSearch: this.filterText(),
      ...this.tenantScope(this.tenantIdComputed()),
      deleted: false,
      page: page,
      size: 10,
      sort: {
        sort: 'internalNumber',
        order: OrderEnum.Asc,
      },
    }));

    if (this.resetVersion() === versionAtStart) {
      this.buildings.update((current) => [...current, ...more]);
    }
  }

  private tenantScope(tenantId: string | undefined): { ownerTenantId?: string; accessibleForTenantId?: string } {
    if (tenantId && this.isExternal() && tenantId === this.ownTenantId()) {
      return { accessibleForTenantId: tenantId };
    }
    return { ownerTenantId: tenantId };
  }
}
